import React, { useState, useEffect, useContext } from "react";
import { fetchWithAuth } from "../../utils/api";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import { ChevronIcon } from "../../assets/icons/icons";
import { AuthorizationContext } from "../ProtectedRoute/ProtectedRoute";
import { useConfig } from "../../contexts/ConfigContext";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import "./style.css";

const EvaluationsList = ({ showRecent = 0 }) => {
  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [urlCopied, setUrlCopied] = useState(false);
  const [acCopied, setAcCopied] = useState(false);
  const { authorization } = useContext(AuthorizationContext);
  const { session } = authorization;
  const { config } = useConfig();
  const API_BASE = config.apiBase;

  useEffect(() => {
    (async () => {
      try {
        // Get user's timezone
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const res = await fetchWithAuth(`${API_BASE}/evaluation`, {
          headers: {
            "X-Timezone": userTimezone,
          },
        });

        // Handle 401 - if fetchWithAuth failed to refresh
        if (res.status === 401) {
          console.warn("🔐 Token expired or invalid");
          localStorage.removeItem("userRole");
          localStorage.removeItem("userPermissions");
          window.location.href = "/login";
          return;
        }

        const json = await res.json();
        if (!res.ok) throw new Error(json.error || res.statusText);
        const baseEvals = json.evaluations || [];

        if (baseEvals.length === 0) {
          setEvaluations([]);
          return;
        }

        const enriched = await Promise.all(
          baseEvals.map(async (ev) => {
            try {
              const { data, error } = await supabase.rpc(
                "get_event_wellness_summary",
                { event_uuid: ev.id },
              );
              if (error || !data) return ev;
              return {
                ...ev,
                overall_wellness_score: data.overall_wellness_score,
                overall_wellness_grade: data.overall_wellness_grade,
              };
            } catch {
              return ev;
            }
          }),
        );

        setEvaluations(enriched);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();

  
  }, [session, API_BASE]);

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const getBadgeColor = (grade, inProgress) =>
    inProgress ? "blue" : config.gradeScale[grade]?.color || "gray";

if (loading) {
  return (
    <div className="evaluations-list-loading-container">
      <div className="evaluations-list-header">
        <h2>Evaluation List</h2>
      </div>
      <div className="small-screen">
        <LoadingSpinner message="Loading evaluations…" fullScreen={false} />
      </div>
    </div>
  );
}
  if (error) return <p className="error-text">Error: {error}</p>;
  if (!evaluations || evaluations.length === 0)
    return (
      <>
        <div className="evaluations-list-header">
          <h2>Evaluation List</h2>
        </div>
        <div className="evaluations-list-container">
          <p>No Evaluations Found</p>
        </div>
      </>
    );

  // only show the 5 most recent
  if (showRecent === 0 || showRecent > evaluations.length)
    showRecent = evaluations.length;
  const evalsToShow = evaluations
    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
    .slice(0, showRecent);
  const getSlugFromUrl = (url) => {
    try {
      const path = new URL(url).pathname; // e.g. "/report-dashboard/slug123"
      const parts = path.split("/");
      return parts[parts.length - 1] || parts[parts.length - 2]; // Handles trailing slash
    } catch {
      return null;
    }
  };

  const handleCopyUrl = (copyText, callback) => {
    navigator.clipboard.writeText(copyText);
    callback(true);
    setTimeout(() => callback(false), 1500); // Hide after 1.5 seconds
  };

  return (
    <>
      {showRecent < evaluations.length ? (
        <div className="evaluations-list-header">
          <h2>Recent Evaluations</h2>
          <button
            className="view-all-button"
            onClick={() => navigate("/my-evaluation")}
          >
            View All
          </button>
        </div>
      ) : (
        <div className="evaluations-list-header">
          <h2>Evaluation List</h2>
        </div>
      )}
      <div className="evaluations-list-container">
        {evalsToShow.map((ev) => {
          // Use status from backend (timezone-aware)
          const inProgress = ev.status === "in_progress";

          return (
            <div key={ev.id} className="evaluation-item">
              {/* Collapsed summary */}
              <div
                className="evaluation-summary"
                onClick={() => toggleExpand(ev.id)}
              >
                <div className="evaluation-left">
                  <p className="evaluation-name">{ev.name}</p>
                  <span
                    className="evaluation-grade-badge"
                    style={{
                      backgroundColor: getBadgeColor(
                        ev.overall_wellness_grade || ev.grade,
                        inProgress,
                      ),
                    }}
                  >
                    {inProgress
                      ? "In progress"
                      : `Grade ${ev.overall_wellness_grade || ev.grade || "—"}`}
                  </span>
                </div>
                <div className="evaluation-right">
                  {expandedId === ev.id ? (
                    <span className="chevron-down">
                      <ChevronIcon />
                    </span>
                  ) : (
                    <span className="chevron-up">
                      <ChevronIcon />
                    </span>
                  )}
                </div>
              </div>

              {/* Expanded details */}
              {expandedId === ev.id && (
                <div className="evaluation-details">
                  {inProgress ? (
                    <p>
                      This evaluation is currently in progress. Please check
                      back after the end date to see results.
                    </p>
                  ) : (
                    <p>
                      Overall Wellness Score ={" "}
                      {ev.overall_wellness_score !== null &&
                        ev.overall_wellness_score !== undefined
                        ? `${ev.overall_wellness_score}/100`
                        : ev.wellness_score_percent !== null &&
                          ev.wellness_score_percent !== undefined
                          ? `${ev.wellness_score_percent}/100`
                          : "N/A"}
                    </p>
                  )}
                  <p>
                    Start Date {new Date(ev.start_date).toLocaleDateString()}
                  </p>
                  <p>End Date {new Date(ev.end_date).toLocaleDateString()}</p>
                  <div className="copy-text-input-container">
                    <label className="evaluation-label">Access Code</label>
                    <input
                      type="text"
                      readOnly
                      value={ev.password}
                      className="copy-input"
                      onClick={() => handleCopyUrl(ev.password, setAcCopied)}
                    />
                    {acCopied && (
                      <div className="clipboard-popup">
                        Access code copied to Clipboard
                      </div>
                    )}
                  </div>
                  <div className="copy-text-input-container">
                    <label className="evaluation-label">Copy URL</label>
                    <br />
                    <input
                      type="text"
                      readOnly
                      value={ev.evaluation_url}
                      className="copy-input"
                      onClick={() =>
                        handleCopyUrl(ev.evaluation_url, setUrlCopied)
                      }
                    />
                    {urlCopied && (
                      <div className="clipboard-popup">
                        URL copied to Clipboard
                      </div>
                    )}
                  </div>
                  {!inProgress && (
                    <div className="action-btn-container">
                      <button
                        className="action-btn"
                        onClick={() =>
                          navigate(
                            `/report-dashboard/${getSlugFromUrl(ev.evaluation_url)}`,
                          )
                        }
                      >
                        See Report
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default EvaluationsList;
